import React, { useState } from 'react'
import pluralize from 'pluralize'

import { stringifyDate, usePet } from '../../modules'

import Modal from '../reusable/modal'
import Receive from '../reusable/receive'

// Shows which batch(es) are available for an item at a store, FEFO-ordered (soonest expiry
// first), and lets staff either let FEFO auto-select (the default, matching today's existing
// behavior) or explicitly override it and pick one. onConfirm receives the chosen batchNo, or
// undefined for "Auto (FEFO)". Already-expired batches are shown (clearly marked) but disabled
// - the backend also rejects them if picked anyway, this just avoids a round-trip.
const IssueBatchPicker = ({ visible, toggle, itemId, storeId, quantity, issuedIn, onConfirm, loading }) => {
    const [selected, setSelected] = useState(null) // null = Auto (FEFO)

    const [{ loading: batchesLoading, error, data: batches }] = usePet('availableBatches', {
        item: itemId,
        store: storeId
    }, { notify: true })

    const confirm = () => {
        onConfirm(selected || undefined)
        setSelected(null)
    }

    return (
        <Modal
            w={ 560 }
            title="Select Batch"
            toggle={ toggle }
            visible={ visible }>
            <p className="text-gray-500 text-sm mb-4">Dispensing { quantity } { pluralize(issuedIn ?? '', quantity) }. FEFO picks the soonest-expiring batch(es) automatically - override only if you need to.</p>
            <Receive loading={ batchesLoading } error={ error } progress>
                <div className="overflow-x-auto mb-4">
                    <table className="table-auto w-full">
                        <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                            <tr>
                                <th className="p-2"><div className="font-semibold text-left"></div></th>
                                <th className="p-2"><div className="font-semibold text-left">Batch</div></th>
                                <th className="p-2"><div className="font-semibold text-left">Quantity</div></th>
                                <th className="p-2"><div className="font-semibold text-left">Expiry</div></th>
                            </tr>
                        </thead>
                        <tbody className="text-sm font-medium divide-y divide-gray-100">
                            <tr>
                                <td className="p-2"><input type="radio" checked={ !selected } onChange={ () => setSelected(null) } /></td>
                                <td className="p-2" colSpan={ 3 }><span className="font-semibold">Auto (FEFO)</span></td>
                            </tr>
                            {
                                (batches ?? []).map(batch => {
                                    const expired = batch.expiry && new Date(batch.expiry) < new Date()
                                    return (
                                        <tr key={ batch.batchNo }>
                                            <td className="p-2">
                                                <input
                                                    type="radio"
                                                    disabled={ expired }
                                                    checked={ selected === batch.batchNo }
                                                    onChange={ () => setSelected(batch.batchNo) } />
                                            </td>
                                            <td className="p-2">{ batch.batchNo }</td>
                                            <td className="p-2">{ batch.total } { pluralize(issuedIn ?? '', batch.total) }</td>
                                            <td className="p-2">
                                                {
                                                    batch.expiry ?
                                                        <span className={ expired ? 'text-red-500 font-semibold' : '' }>
                                                            { stringifyDate(batch.expiry, true) }{ expired && ' (expired)' }
                                                        </span>
                                                        : '—'
                                                }
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </Receive>
            <div className="flex justify-end">
                <button
                    disabled={ loading }
                    onClick={ confirm }
                    className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                    { selected ? `Issue From ${selected}` : 'Issue (Auto FEFO)' }
                </button>
            </div>
        </Modal>
    )
}

export default IssueBatchPicker
